import { useMemo, useState } from 'react'
import { formatShortDate, weekdayName } from '@app/shared'

import { useClock, useCreateTask, useTasks, useToggleTask } from '../lib/data'
import { DataModeBadge } from '../components/HeaderBand'
import { SkeletonRows } from '../components/Card'
import {
  buildBoard,
  doneDays,
  DONE_LOG_KEPT_DAYS,
  isEmptyLens,
  overdueSummary,
  UNFILED,
  type LensId,
} from '../components/tasks/derive'
import { ArrowRight, Warning } from '../components/tasks/icons'
import { LENSES, LensColumn } from '../components/tasks/LensColumn'
import { QuickAdd } from '../components/tasks/QuickAdd'
import { DoneLog } from '../components/tasks/DoneLog'
import { DayGroupedRows, DoneRows, FlatRows, TaskCard } from '../components/tasks/TaskRow'
import { TaskMenuHost } from '../components/tasks/TaskEditMenu'
import { PendingControl, TasksBand, Unavailable } from '../components/tasks/parts'

/**
 * REMINDERS — docs/design/handoff/"LifeOS Tasks.dc.html", option 8a.
 *
 * "Replace the phone's reminders app" (README §7), and the design's argument is that a reminders
 * app is boring on purpose: lenses left, ONE column of rows in the middle, the done log right.
 *
 * THE SCREEN IS CALLED REMINDERS AND NOTHING ELSE MOVED (owner, 2026-08-28). The rail said Tasks
 * while the Today card said Reminders, for one list of rows. The word is now Reminders every place
 * a person reads it; the route (`/tasks`), the Worker paths (`/api/tasks`), the table, the types
 * and the `tasks` hue token are all unchanged, because a machine-readable contract that renames
 * itself for a label is a contract that breaks every reader it has. A ROW is still a "task": the
 * noun for one item did not change, only the name of the place they live.
 *
 * DELIBERATELY ABSENT, and this is a rule rather than an omission (README §7): no progress
 * rings, no streaks, no completion percentages, no celebration of any kind. Finishing a task
 * moves it to the done log and that is all that happens. If a future edit adds a ring here, that
 * edit is the bug.
 *
 * OVERDUE IS PINNED AND NEVER SOFTENED. Its card sits above every group in every lens, it keeps
 * its red, and it is counted separately from the header's "open" so it can never be diluted into
 * a larger, friendlier number.
 *
 * AND IT BELONGS TO TODAY. Pinned everywhere, but OWNED by one lens — the Today count includes
 * it and the Today lens never prints "nothing is due" over it (derive.ts → lensOwnsOverdue).
 * Before that was true, a board whose only open task was overdue counted it in the header and
 * showed it in no bucket at all: a number nobody could tick (owner report, 2026-08-28).
 *
 * WHAT IS REAL TODAY. Reading, completing, adding, and now EDITING are real in BOTH modes: the
 * demo dataset is mutable, and remote mode talks to apps/worker/src/routes/tasks.ts — one table,
 * owner-scoped, with recurrence expanded on completion. Each row carries a menu that sets or
 * clears its due date, moves it to another list, and deletes it (TaskEditMenu.tsx). What remains
 * unbuilt is UI, not backend: the list filter, the sort picker and the bulk reschedule each say
 * which version brings them rather than pretending to work. When a read genuinely fails, the
 * three columns print the Worker's own sentence instead of rendering an empty board — an empty
 * board would claim "you have nothing to do", which is a different and false statement.
 */
export function Tasks() {
  const { data: clock } = useClock()
  const { data: tasks, isPending, error } = useTasks()
  const toggle = useToggleTask()
  const create = useCreateTask()
  const [lens, setLens] = useState<LensId>('today')

  const today = clock?.date
  const board = useMemo(
    () => (tasks && today ? buildBoard(tasks, today) : undefined),
    [tasks, today],
  )
  // The Done log lens's own rows, center-pane side — the exact grouping doneDays() gives the
  // rail, computed once and handed to both so they can never disagree about which day something
  // finished on.
  const doneRows = useMemo(
    () => (tasks && today ? doneDays(tasks, today) : []),
    [tasks, today],
  )
  // The board's own list names, memoised so the quick-add's parse memo has a stable dependency.
  const listNames = useMemo(() => board?.lists.map((l) => l.name) ?? [], [board])

  // One sentence, reused: when the read threw, the writes are gone too, and every control on the
  // screen should say the same thing about why.
  const blockedReason =
    error instanceof Error
      ? error.message
      : error
        ? 'Reminders could not be read, so nothing here can be changed.'
        : undefined

  return (
    <>
      <TasksBand>
        <div className="flex items-center gap-[14px]">
          <span className="flex-none text-screen font-semibold">Reminders</span>
          {board && today && (
            <span
              className="flex items-center gap-[9px] whitespace-nowrap text-[11.5px] font-normal leading-none text-muted"
              title={
                'Open: due today or inside the next seven days. Overdue is counted separately, ' +
                'never folded in — a task that is six days late must not disappear into a bigger number. ' +
                'Undated work is under Someday and claims no day, so it is not counted here.'
              }
            >
              <span>{board.openCount} open</span>
              <Dot />
              <span>{board.overdue.length} overdue</span>
              <Dot />
              <span>
                {weekdayName(today, true)} {formatShortDate(today)}
              </span>
            </span>
          )}

          <div className="ml-auto flex flex-none items-center gap-[7px]">
            <PendingControl
              label="All lists"
              waitingFor="Filtering the board to one list arrives with V6 (Reminders). The lists in the left column already show what is in each one."
              className="rounded-pill border border-line bg-surface px-[11px] py-[6px] text-[11px] font-medium text-ink-3"
            />
            <PendingControl
              label="Sort: due"
              waitingFor="Choosing a sort arrives with V6 (Reminders). Everything on this board is ordered by due date, soonest first."
              className="rounded-pill border border-line bg-surface px-[11px] py-[6px] text-[11px] font-medium text-ink-3"
            />
            <DataModeBadge />
          </div>
        </div>

        {/* `today` and `lists` are what the phrase parser resolves against: without a clock
            nothing relative is read, and a `#tag` can only ever name a list that already
            exists — typing one has never created a list and does not start now. */}
        <QuickAdd
          disabledReason={blockedReason}
          today={today}
          lists={listNames}
          onAdd={({ title, due, list, reminderAt, rrule }) =>
            create.mutate({ title, list: list ?? UNFILED, due, reminderAt, rrule })
          }
        />
      </TasksBand>

      {/* ONE MENU OPEN AT A TIME, and the host is what owns that rule — a row only knows about
          itself. It wraps all three columns so the panel is not clipped by the middle column's
          own `overflow-y-auto`; the panel is `position: fixed` anyway, anchored to the button's
          measured rectangle. */}
      <TaskMenuHost
        lists={listNames}
        today={today}
        disabledReason={blockedReason}
      >
        <div className="flex min-h-0 flex-1 gap-[13px] overflow-hidden px-[30px] pb-[26px]">
          <LensColumn board={board} active={lens} onSelect={setLens} />

          <div className="flex min-w-0 flex-1 flex-col gap-[11px] overflow-y-auto">
            {isPending && (
              <div className="rounded-card border border-line bg-surface px-4 py-4 shadow-card">
                <SkeletonRows rows={5} />
              </div>
            )}
            <Unavailable error={error} />

            {board && today && (
              <>
                {/* PINNED, IN EVERY LENS. An overdue task is not the Today lens's private problem. */}
                {board.overdue.length > 0 && lens !== 'done' && (
                  <TaskCard
                    tone="danger"
                    header={
                      <header className="flex items-center gap-[9px] bg-danger-fill px-4 py-[11px]">
                        <Warning size={14} className="flex-none text-danger-2" />
                        <h2 className="text-[12px] font-semibold leading-none text-danger-2">
                          Overdue
                        </h2>
                        <span className="text-[11px] font-normal leading-none text-danger-dot">
                          {overdueSummary(board.overdue, today)}
                        </span>
                        <PendingControl
                          label={board.overdue.length === 2 ? 'Reschedule both' : 'Reschedule all'}
                          waitingFor="Rescheduling in bulk arrives with V6 (Reminders). Until then each row can be finished from its own checkbox."
                          className="ml-auto text-[11px] font-semibold text-danger-2"
                        />
                      </header>
                    }
                  >
                    <FlatRows
                      tasks={board.overdue}
                      today={today}
                      density="overdue"
                      onToggle={toggle.mutate}
                      disabledReason={blockedReason}
                    />
                  </TaskCard>
                )}

                {lens === 'today' && (
                  <>
                    {board.dueToday.length > 0 && (
                      <TaskCard label="Today" qualifier={items(board.dueToday.length)}>
                        <FlatRows
                          tasks={board.dueToday}
                          today={today}
                          onToggle={toggle.mutate}
                          disabledReason={blockedReason}
                        />
                      </TaskCard>
                    )}
                    {board.soon.length > 0 && (
                      <TaskCard label="Upcoming" qualifier="next 7 days">
                        <DayGroupedRows
                          tasks={board.soon}
                          today={today}
                          onToggle={toggle.mutate}
                          disabledReason={blockedReason}
                        />
                      </TaskCard>
                    )}
                  </>
                )}

                {lens === 'inbox' && board.inbox.length > 0 && (
                  <TaskCard label="Inbox" qualifier={items(board.inbox.length)}>
                    <FlatRows
                      tasks={board.inbox}
                      today={today}
                      onToggle={toggle.mutate}
                      disabledReason={blockedReason}
                    />
                  </TaskCard>
                )}

                {lens === 'upcoming' && board.soon.length + board.later.length > 0 && (
                  <TaskCard
                    label="Upcoming"
                    qualifier={items(board.soon.length + board.later.length)}
                  >
                    <DayGroupedRows
                      tasks={[...board.soon, ...board.later]}
                      today={today}
                      onToggle={toggle.mutate}
                      disabledReason={blockedReason}
                    />
                  </TaskCard>
                )}

                {lens === 'someday' && board.someday.length > 0 && (
                  <TaskCard
                    label="Someday"
                    qualifier={items(board.someday.length)}
                  >
                    <FlatRows
                      tasks={board.someday}
                      today={today}
                      onToggle={toggle.mutate}
                      disabledReason={blockedReason}
                    />
                  </TaskCard>
                )}

                {/* The Done log lens now RENDERS the log — it used to only point at the rail
                    (owner report, 2026-08-28: selecting it showed an explainer card and nothing
                    else in the middle column). `doneRows` is the same `doneDays()` grouping the
                    rail itself draws from, so this is a second RENDERING of one list, not a
                    second list to drift out of step with it. The rail keeps its 90-day window and
                    its `All` toggle — this pane is a wider look at what it already shows. */}
                {lens === 'done' && (
                  <TaskCard
                    label="Done log"
                    qualifier={items((tasks ?? []).filter((t) => t.done).length) + ' kept'}
                  >
                    <div className="px-4 pb-2 pt-1">
                      <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
                        Kept for {DONE_LOG_KEPT_DAYS} days, then archived to the journal.
                      </p>
                    </div>
                    {doneRows.length > 0 ? (
                      <DoneRows days={doneRows} />
                    ) : (
                      <div className="px-4 pb-4">
                        <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
                          Nothing finished yet.
                        </p>
                      </div>
                    )}
                  </TaskCard>
                )}

                {isEmptyLens(board, lens) && (
                  <div className="rounded-card border border-dashed border-dash bg-surface-3 px-4 py-[15px]">
                    <p className="text-[12px] leading-[1.45] text-ink-3 text-pretty">
                      {LENSES.find((l) => l.id === lens)?.empty}
                    </p>
                  </div>
                )}

                {/* The unfiled strip. It is a POINTER, not a claim: it says how many arrived with
                    no list and takes you to them. Absent when there is nothing unfiled. */}
                {board.inbox.length > 0 && lens !== 'inbox' && (
                  <button
                    type="button"
                    onClick={() => setLens('inbox')}
                    className="mt-auto flex flex-none items-center gap-[10px] rounded-banner border border-dashed border-dash px-[15px] py-[10px] text-left transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-3"
                  >
                    <span className="text-[12px] font-medium leading-none text-ink-3">
                      {board.inbox.length} unfiled in {UNFILED}
                    </span>
                    <ArrowRight size={13} className="flex-none text-muted" />
                    <span className="ml-auto text-[10.5px] font-normal leading-none text-faintest-2">
                      captured by voice or widget, no list yet
                    </span>
                  </button>
                )}
              </>
            )}
          </div>

          {today && (
            <DoneLog tasks={tasks} today={today} isPending={isPending} error={error} />
          )}
        </div>
      </TaskMenuHost>
    </>
  )
}

function Dot() {
  return <span className="size-[3px] flex-none rounded-full bg-dash" aria-hidden />
}

/** '5 items' — the group header's quiet half. Singular is not a rounding error. */
function items(n: number): string {
  return `${n} ${n === 1 ? 'item' : 'items'}`
}

